import React, {useState, useEffect} from "react"
import {
	Box,
	Grid,
	Typography,
	CircularProgress,
	Chip,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	Avatar,
	Divider,
} from "@mui/material"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import ArchiveIcon from "@mui/icons-material/Archive"
import SmartToyIcon from "@mui/icons-material/SmartToy"
import styled from "styled-components"

import {NoteForm, Form, Field, FieldAnswered, Upload} from "./NoteForm"

import {api} from "@indebted/api"
import {useNotification} from "@indebted/components/Notification"
import {linkifyText} from "@indebted/components/safeHTML"
import {colors} from "@indebted/theme/colors"

const localTimeOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true,
	timeZoneName: "short",
} as const

function toLocalTime(dateString: string) {
	return new Intl.DateTimeFormat("default", localTimeOptions).format(new Date(dateString))
}

type NoteStatus = {
	ID: string
	Label: string
}

type NoteArchiveStatus = {
	ID: string
	Label: string
}

type Note = {
	ID: string
	Label?: string
	CustomerID: string
	Form: {
		Type: string
		Version: number
		Fields: FieldAnswered[]
	}
	Uploads: Upload[]
	AgentName: string
	AgentEmail: string
	Status?: NoteStatus
	ArchiveStatuses?: NoteArchiveStatus[]
	CreatedAt: string
}

type NotesResponse = {
	Forms: Form[]
	Notes: Note[]
}

const isTextareaField = (field: Field | FieldAnswered) => field.Type === "textarea"
const isBooleanField = (field: Field | FieldAnswered) => field.Type === "boolean"
const isDateField = (field: Field | FieldAnswered) => field.Type === "date"
const isDateTimeField = (field: Field | FieldAnswered) => field.Type === "datetime"
const isURLField = (field: Field | FieldAnswered) => field.Type === "url"

const isSafeHTTPURL = (value: string) => {
	try {
		const u = new URL(value)
		return u.protocol === "http:" || u.protocol === "https:"
	} catch {
		return false
	}
}

const dateDisplayOptions = {year: "numeric", month: "short", day: "numeric"} as const

const dateTimeDisplayOptions = {
	year: "numeric",
	month: "short",
	day: "numeric",
	hour: "numeric",
	minute: "numeric",
	hour12: true,
	timeZoneName: "short",
} as const

function formatDate(value: string) {
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return value
	return new Intl.DateTimeFormat("default", dateDisplayOptions).format(parsed)
}

function formatDateTime(value: string) {
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return value
	return new Intl.DateTimeFormat("default", dateTimeDisplayOptions).format(parsed)
}

function NoteItemCard({note, onArchived}: {note: Note; onArchived?: () => void}) {
	const {notification} = useNotification()
	const [archiveAnchor, setArchiveAnchor] = useState<HTMLElement | null>(null)
	const [archiving, setArchiving] = useState(false)
	const hasUploads = note.Uploads?.length > 0
	const isArchived = note.Status && note.Status.ID !== "active"
	const canArchive = !isArchived && note.ArchiveStatuses && note.ArchiveStatuses.length > 0

	const handleArchive = async (statusID: string) => {
		setArchiveAnchor(null)
		setArchiving(true)
		try {
			await api.customer.archiveNote(note.CustomerID, note.ID, {Status: statusID})
			notification.success("Note archived")
			if (onArchived) onArchived()
		} catch (error: any) {
			notification.error(error.Message || "Failed to archive note")
		} finally {
			setArchiving(false)
		}
	}

	const handleDownloadUpload = (upload: Upload) => {
		window.open(api.customer.getNoteUploadContentURL(note.CustomerID, note.ID, upload.ID), "_blank")
	}

	const renderFieldContent = (field: FieldAnswered) => {
		if (isTextareaField(field)) {
			return (
				<NoteTypography
					variant="body2"
					dangerouslySetInnerHTML={{__html: linkifyText(String(field.Content))}}
				/>
			)
		}
		if (isBooleanField(field)) {
			return <Typography variant="body2">{field.Content ? "Yes" : "No"}</Typography>
		}
		if (isDateField(field)) {
			return <Typography variant="body2">{formatDate(String(field.Content))}</Typography>
		}
		if (isDateTimeField(field)) {
			return <Typography variant="body2">{formatDateTime(String(field.Content))}</Typography>
		}
		if (isURLField(field)) {
			const value = String(field.Content)
			if (isSafeHTTPURL(value)) {
				return (
					<Typography variant="body2">
						<a href={value} target="_blank" rel="noopener noreferrer">
							{value}
						</a>
					</Typography>
				)
			}
			return <Typography variant="body2">{value}</Typography>
		}
		return <Typography variant="body2">{String(field.Content)}</Typography>
	}

	const metadataFields = note.Form.Fields.filter((field) => !isTextareaField(field))
	const narrativeFields = note.Form.Fields.filter(isTextareaField)
	const isHumanAuthor = note.AgentEmail.includes("@")

	return (
		<Box display="flex" gap={1.5}>
			<Avatar
				sx={{
					width: 32,
					height: 32,
					bgcolor: colors.warning["50"],
					color: colors.warning["800"],
					border: `1px solid ${colors.warning["200"]}`,
					fontSize: 13,
					fontWeight: 600,
				}}
			>
				{isHumanAuthor ? agentInitials(note.AgentName) : <SmartToyIcon fontSize="small" />}
			</Avatar>
			<Box
				flex={1}
				minWidth={0}
				position="relative"
				overflow="hidden"
				border="1px solid"
				borderColor="grey.200"
				borderRadius={2}
				bgcolor="background.paper"
				p={2.5}
			>
				{isArchived && <ArchivedOverlay />}
				<Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1} mb={1}>
					<Box display="flex" alignItems="center" gap={1} minWidth={0} flexWrap="wrap">
						<Typography variant="subtitle2" color="textPrimary">
							{note.AgentName}
						</Typography>
						{note.Form.Type !== "generic" && (
							<Chip
								label={note.Label || note.Form.Type}
								size="small"
								sx={{
									bgcolor: colors.warning["50"],
									color: colors.warning["800"],
									border: `1px solid ${colors.warning["200"]}`,
								}}
							/>
						)}
						{isArchived && <Chip label={note.Status!.Label} size="small" color="warning" />}
					</Box>
					<Box display="flex" alignItems="center" gap={1}>
						<Typography variant="caption" color="textSecondary" whiteSpace="nowrap">
							<time>{toLocalTime(note.CreatedAt)}</time>
						</Typography>
						{canArchive && (
							<>
								<Tooltip title="Archive note">
									<IconButton
										size="small"
										onClick={(e) => setArchiveAnchor(e.currentTarget)}
										disabled={archiving}
									>
										{archiving ? <CircularProgress size={16} /> : <ArchiveIcon fontSize="small" />}
									</IconButton>
								</Tooltip>
								<Menu
									anchorEl={archiveAnchor}
									open={Boolean(archiveAnchor)}
									onClose={() => setArchiveAnchor(null)}
								>
									{note.ArchiveStatuses!.map((status) => (
										<MenuItem key={status.ID} onClick={() => handleArchive(status.ID)}>
											{status.Label}
										</MenuItem>
									))}
								</Menu>
							</>
						)}
					</Box>
				</Box>

				{isArchived ? (
					<Typography variant="body2" color="textSecondary" fontStyle="italic">
						Not Available
					</Typography>
				) : (
					<Box display="flex" flexDirection="column" gap={1.5} maxWidth="90ch">
						{metadataFields.length > 0 && (
							<Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(200px, 1fr))" gap={1.5}>
								{metadataFields.map((field, idx) => (
									<Box key={idx}>
										<Typography variant="caption" color="textSecondary" display="block">
											{field.Name}
										</Typography>
										<Box sx={{"& .MuiTypography-root": {fontWeight: 500}}}>
											{renderFieldContent(field)}
										</Box>
									</Box>
								))}
							</Box>
						)}
						{narrativeFields.map((field, idx) => (
							<Box key={idx}>
								{field.Name !== "Note" && (
									<Typography variant="caption" color="textSecondary" display="block">
										{field.Name}
									</Typography>
								)}
								{renderFieldContent(field)}
							</Box>
						))}
					</Box>
				)}

				{hasUploads && (
					<>
						<Divider sx={{my: 1.5}} />
						<Typography variant="caption" color="textSecondary" display="block" mb={0.75}>
							Attachments
						</Typography>
						<Box display="flex" flexWrap="wrap" gap={1}>
							{note.Uploads.map((upload) => (
								<Chip
									key={upload.ID}
									label={upload.FileName}
									size="small"
									icon={<AttachFileIcon />}
									onClick={isArchived ? undefined : () => handleDownloadUpload(upload)}
									sx={isArchived ? {cursor: "default", opacity: 0.6} : undefined}
								/>
							))}
						</Box>
					</>
				)}
			</Box>
		</Box>
	)
}

function agentInitials(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0].toUpperCase())
		.join("")
}

const NoteTypography = styled(Typography)`
	white-space: break-spaces;
	line-height: 1.6;

	a {
		word-break: break-all;
	}
`

const ArchivedOverlay = styled.div`
	position: absolute;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.04);
	pointer-events: none;
	z-index: 1;
`

function NotesV2({
	customerID,
	reload,
	systemOfRecord,
}: {
	customerID: string
	reload: () => void
	systemOfRecord: string
}) {
	const {notification} = useNotification()
	const [loading, setLoading] = useState(true)
	const [notesData, setNotesData] = useState<NotesResponse | null>(null)

	const isLatitude = systemOfRecord === "Latitude"

	const loadNotes = () => {
		setLoading(true)
		api.customer
			.getNotes(customerID)
			.then((data) => {
				setNotesData(data)
			})
			.catch(() => {
				notification.error("Failed to load notes")
			})
			.finally(() => {
				setLoading(false)
			})
	}

	useEffect(() => {
		let isMounted = true

		const loadNotesWithGuard = () => {
			setLoading(true)
			api.customer
				.getNotes(customerID)
				.then((data) => {
					if (isMounted) {
						setNotesData(data)
					}
				})
				.catch(() => {
					if (isMounted) {
						notification.error("Failed to load notes")
					}
				})
				.finally(() => {
					if (isMounted) {
						setLoading(false)
					}
				})
		}

		loadNotesWithGuard()

		return () => {
			isMounted = false
		}
	}, [customerID, notification])

	const handleNoteAdded = () => {
		loadNotes()
		reload()
	}

	if (loading) {
		return (
			<Box p={3} display="flex" justifyContent="center">
				<CircularProgress />
			</Box>
		)
	}

	if (!notesData) {
		return (
			<Box p={3}>
				<Typography color="error">Failed to load notes</Typography>
			</Box>
		)
	}

	return (
		<Box p={3}>
			<NoteForm
				customerID={customerID}
				forms={notesData.Forms}
				disabled={isLatitude}
				onSuccess={handleNoteAdded}
			/>

			<Typography variant="subtitle1">{notesData.Notes.length === 0 ? "No notes" : "Notes"}</Typography>
			<Grid direction="column" spacing={2} container>
				{notesData.Notes.map((note, idx) => (
					<Grid key={idx}>
						<NoteItemCard note={note} onArchived={loadNotes} />
					</Grid>
				))}
			</Grid>
		</Box>
	)
}

export {NotesV2, NoteItemCard}
export type {Note, NotesResponse}
